import { fromEvent, identity, merge, Observable, OperatorFunction, pipe } from 'rxjs';
import {
  bufferToggle,
  filter,
  first,
  map,
  mapTo,
  mergeAll,
  share,
  startWith,
  switchMap,
  windowToggle,
} from 'rxjs/operators';
import { isKarmaTestEnv } from './env';
import { fromMutationObserver } from './fromMutationObserver';
import { retryDelay } from './retryDelay';

export type MutationFrame = {
  observer: MutationObserver;
  records: MutationRecord[];
};

export type MutationObservableOptions = {
  filterMutationRecord?: (record: MutationRecord) => boolean;
  performCascadingMutations?: (frame: MutationFrame) => void;
};

/** Creates an Observable version of MutationObserver for an element. */
export function fromMutations(
  element: HTMLElement,
  options: MutationObservableOptions = {},
): Observable<MutationFrame> {
  // Emits when the underlying MutationObserver calls back. Shared to avoid creating duplicate
  // MutationObservers.
  const frames$ = fromMutationObserver(element, {
    attributeOldValue: true,
    attributes: true,
    characterData: true,
    characterDataOldValue: true,
    childList: true,
    subtree: true,
  }).pipe(
    map(([records, observer]) => ({ observer, records })),
    share(),
  );

  // When a composition starts, emits an Observable that emits when a composition ends.
  // TODO: Fix duplication with ignoreDuringCompositions helper.
  const compositions$ = fromEvent(element, 'compositionstart').pipe(
    // TODO: Make sure all browsers send compositionend in all cases to avoid buffering forever.
    map(() => merge(fromEvent(element, 'compositionend'), fromEvent(element, 'blur'))),
  );

  // Combination of all situations in which we need to buffer mutations. (Currently only during compositions.)
  // Shared to ensure consistency between bufferOpens$ and bufferCloses$, and to avoid attaching duplicate
  // underlying event listeners.
  const buffers$ = merge(compositions$).pipe(share());

  // Emits when we need to start buffering mutations. Shared to ensure consistency between the buffered and
  // unbuffered observables.
  const bufferOpens$ = buffers$.pipe(
    mapTo(undefined),
    // tap(() => console.log('Buffering DOM mutations')),
    share(),
  );

  // Emits when we need to stop buffering mutations. Shared to ensure consistency between the buffered and
  // unbuffered observables, and to avoid attaching duplicate underlying event listeners.
  const bufferCloses$ = buffers$.pipe(
    switchMap(closes$ => closes$.pipe(mapTo(undefined), first())),
    share(),
  );

  // Emits DOM mutaton events/frames immediately, but only if we're not buffering.
  const unbufferedFrames$ = frames$.pipe(
    // Use arguments opposite of bufferToggle() below.
    windowToggle(
      // Start a window immediately.
      bufferCloses$.pipe(startWith(undefined)),
      () => bufferOpens$,
    ),
    mergeAll(),
  );

  // Emits buffered DOM mutation events/frames.
  const bufferedFrames$ = frames$.pipe(
    bufferToggle(bufferOpens$, () => bufferCloses$),
    // tap(frames => console.log('Done buffering DOM mutations, frame count is', frames.length)),
    flattenFrames(),
  );

  // Post-process. Shared to ensure side effects happen once.
  return merge(unbufferedFrames$, bufferedFrames$).pipe(
    filterRecords(options.filterMutationRecord),
    filter(({ records }) => records.length > 0),
    performCascadingMutations(options.performCascadingMutations, options.filterMutationRecord),
    isKarmaTestEnv ? identity : retryDelay(),
    share(),
  );
}

function flattenFrames(): OperatorFunction<MutationFrame[], MutationFrame> {
  return pipe(
    filter(frames => frames.length > 0),
    map(frames => ({
      ...frames[0],
      records: Array.prototype.concat(...frames.map(frame => frame.records)),
    })),
  );
}

function filterRecords(
  predicate?: (record: MutationRecord) => boolean,
): OperatorFunction<MutationFrame, MutationFrame> {
  if (!predicate) {
    return identity;
  }

  return map(({ records, ...event }) => ({
    ...event,
    records: records.filter(predicate),
  }));
}

function performCascadingMutations(
  callback?: (frame: MutationFrame) => void,
  filterPredicate?: (record: MutationRecord) => boolean,
): OperatorFunction<MutationFrame, MutationFrame> {
  if (!callback) {
    return identity;
  }

  return map(frame => {
    const { observer, records } = frame;

    callback(frame);

    // Take the DOM mutations from the processing/normalization to prevent an event cascade.
    const cascadingRecords = observer.takeRecords();
    const filteredCascadingRecords = filterPredicate
      ? cascadingRecords.filter(filterPredicate)
      : cascadingRecords;

    return {
      observer,
      records: records.concat(filteredCascadingRecords),
    };
  });
}
